// Tesla Fleet API wrapper - uses Vercel proxy to avoid CORS
import { getTeslaAccessToken } from './teslaAuth';

// Make authenticated API request via our proxy
async function teslaRequest(endpoint) {
    const token = await getTeslaAccessToken();
    if (!token) {
        throw new Error('Not authenticated with Tesla');
    }

    // Use our Vercel serverless function to proxy the request
    const response = await fetch('/api/tesla', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            endpoint: endpoint,
            accessToken: token,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || `Tesla API error: ${response.status}`);
    }

    return response.json();
}

// Get list of vehicles
export async function getVehicles() {
    const data = await teslaRequest('/api/1/vehicles');
    return data.response || [];
}

// Get vehicle by ID
export async function getVehicle(vehicleId) {
    const data = await teslaRequest(`/api/1/vehicles/${vehicleId}`);
    return data.response;
}

// Get vehicle data (includes location, state, etc.)
export async function getVehicleData(vehicleId) {
    const data = await teslaRequest(`/api/1/vehicles/${vehicleId}/vehicle_data`);
    return data.response;
}

// Get first vehicle's name (convenience function)
export async function getMyCarName() {
    try {
        const vehicles = await getVehicles();
        if (vehicles.length === 0) {
            return null;
        }
        return vehicles[0].display_name || vehicles[0].vin;
    } catch (error) {
        console.error('Failed to get car name:', error);
        return null;
    }
}

// Get navigation destination (for future use)
export async function getNavigationData(vehicleId) {
    try {
        const data = await getVehicleData(vehicleId);
        return data.drive_state;
    } catch (error) {
        console.error('Failed to get navigation data:', error);
        return null;
    }
}

// Check arrival status - returns minutes to destination or null if no active nav
export async function getArrivalInfo() {
    try {
        const vehicles = await getVehicles();
        if (vehicles.length === 0) {
            return null;
        }

        const vehicleId = vehicles[0].id;
        const vehicleData = await getVehicleData(vehicleId);

        if (!vehicleData?.drive_state) {
            return null;
        }

        const driveState = vehicleData.drive_state;

        // Check if there's an active route
        // Tesla provides: active_route_destination, active_route_minutes_to_arrival
        if (driveState.active_route_destination && driveState.active_route_minutes_to_arrival !== undefined) {
            return {
                destination: driveState.active_route_destination,
                minutesToArrival: driveState.active_route_minutes_to_arrival,
                trafficMinutesDelay: driveState.active_route_traffic_minutes_delay || 0,
            };
        }

        return null;
    } catch (error) {
        console.error('Failed to get arrival info:', error);
        return null;
    }
}

